import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Warning as WarningIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { api, apiRoutes } from '../../../api';

const IssueReporting = () => {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIssue, setEditingIssue] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [formData, setFormData] = useState({
    location: '',
    category: '',
    severity: 'medium',
    comment: ''
  });

  useEffect(() => {
    loadIssues();
  }, []);

  const loadIssues = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      if (!token) return;

      const response = await api.get(apiRoutes.agentIssues.list, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIssues(response.data.results || []);
    } catch (err) {
      setError('بارگذاری مشکلات ناموفق بود');

    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.location || !formData.category || !formData.comment.trim()) {
      setError('لطفاً تمام فیلدهای مورد نیاز را پر کنید');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const payload = {
        location: {
          type: 'Point',
          coordinates: formData.location.split(',').map(coord => parseFloat(coord.trim()))
        },
        category: formData.category,
        severity: formData.severity,
        comment: formData.comment
      };

      const token = localStorage.getItem('access_token');
      if (editingIssue) {
        await api.put(apiRoutes.agentIssues.detail(editingIssue.id), payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSuccess('تغییرات برای بررسی تیم GIS ارسال شد.');
      } else {
        await api.post(apiRoutes.agentIssues.list, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSuccess('گزارش برای بررسی تیم GIS ارسال شد.');
      }

      loadIssues();
      handleCloseDialog();

    } catch (err) {
      setError(err.response?.data?.detail || 'ناموفق در ذخیره مشکل');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (issue) => {
    setEditingIssue(issue);
    setFormData({
      location: `${issue.location.coordinates[0]}, ${issue.location.coordinates[1]}`,
      category: issue.category,
      severity: issue.severity,
      comment: issue.comment
    });
    setDialogOpen(true);
  };

  const handleDelete = async (issueId) => {
    if (!window.confirm('آیا مطمئن هستید که می‌خواهید این مشکل را حذف کنید؟')) {
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      await api.delete(apiRoutes.agentIssues.detail(issueId), {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('مشکل با موفقیت حذف شد!');
      loadIssues();
    } catch (err) {
      setError('ناموفق در حذف مشکل');
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingIssue(null);
    setFormData({
      location: '',
      category: '',
      severity: 'medium',
      comment: ''
    });
    setError(null);
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'low':
        return 'success';
      case 'medium':
        return 'warning';
      case 'high':
        return 'error';
      case 'critical':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'reported':
        return 'info';
      case 'in_progress':
        return 'warning';
      case 'resolved':
        return 'success';
      case 'closed':
        return 'default';
      default:
        return 'default';
    }
  };

  const getReviewColor = (status) => ({ approved: 'success', rejected: 'error', pending_review: 'warning' }[status] || 'default');
  const getReviewLabel = (status) => ({ approved: 'تأیید شده', rejected: 'رد شده', pending_review: 'در انتظار GIS', superseded: 'جایگزین شده' }[status] || 'ارسال نشده');

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fa-IR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box dir="rtl" className="agents-section agents-issues">
      <Box className="agents-section-head" display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" gutterBottom>
            گزارش مشکلات
          </Typography>
          <Typography variant="body2" color="text.secondary">
            گزارش و پیگیری مشکلات و مشاهدات میدانی
          </Typography>
        </Box>
        <Box>
          <Tooltip title="تازه‌سازی">
            <IconButton onClick={loadIssues} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setDialogOpen(true)}
            sx={{ ml: 1 }}
          >
            گزارش مشکل
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Issues Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            مشکلات گزارش‌شده
          </Typography>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>دسته</TableCell>
                  <TableCell>توضیح</TableCell>
                  <TableCell>شدت</TableCell>
                  <TableCell>وضعیت</TableCell>
                  <TableCell>بررسی GIS</TableCell>
                  <TableCell>مکان</TableCell>
                  <TableCell>گزارش‌شده</TableCell>
                  <TableCell>اقدامات</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {issues.map((issue) => (
                  <TableRow key={issue.id}>
                    <TableCell>
                      <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                        {issue.category.replace('_', ' ')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {issue.comment.length > 50
                          ? `${issue.comment.substring(0, 50)}...`
                          : issue.comment
                        }
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={issue.severity}
                        color={getSeverityColor(issue.severity)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={issue.status}
                        color={getStatusColor(issue.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title={issue.review_notes || ''}>
                        <Chip label={getReviewLabel(issue.review_status)} color={getReviewColor(issue.review_status)} size="small" />
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {issue.location.coordinates[1].toFixed(4)}, {issue.location.coordinates[0].toFixed(4)}
                      </Typography>
                      {issue.neighborhood_name && (
                        <Typography variant="caption" color="text.secondary">
                          {issue.neighborhood_name}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(issue.created_at)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip title="ویرایش">
                        <IconButton size="small" onClick={() => handleEdit(issue)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="حذف">
                        <IconButton size="small" color="error" onClick={() => handleDelete(issue.id)}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {issues.length === 0 && (
            <Box textAlign="center" py={4}>
              <WarningIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                هیچ مشکلی گزارش نشده
              </Typography>
              <Typography variant="body2" color="text.secondary">
                روی "گزارش مشکل" کلیک کنید تا اولین مشاهده میدانی خود را اضافه کنید.
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Issue Reporting Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle>
            {editingIssue ? 'ویرایش مشکل' : 'گزارش مشکل جدید'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="مکان (طول جغرافیایی، عرض جغرافیایی)"
                  placeholder="51.3890, 35.6892"
                  value={formData.location}
                  onChange={(e) => handleFormChange('location', e.target.value)}
                  helperText="مختصات را به صورت: طول جغرافیایی، عرض جغرافیایی وارد کنید"
                  required
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>دسته مشکل</InputLabel>
                  <Select
                    value={formData.category}
                    onChange={(e) => handleFormChange('category', e.target.value)}
                  >
                    <MenuItem value="garbage">زباله/پسماند</MenuItem>
                    <MenuItem value="fighting">درگیری/خشونت</MenuItem>
                    <MenuItem value="noise">آلودگی صوتی</MenuItem>
                    <MenuItem value="traffic">مشکلات ترافیکی</MenuItem>
                    <MenuItem value="infrastructure">مشکلات زیرساختی</MenuItem>
                    <MenuItem value="safety">نگرانی‌های ایمنی</MenuItem>
                    <MenuItem value="environmental">مسائل زیست‌محیطی</MenuItem>
                    <MenuItem value="other">سایر</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>شدت</InputLabel>
                  <Select
                    value={formData.severity}
                    onChange={(e) => handleFormChange('severity', e.target.value)}
                  >
                    <MenuItem value="low">کم</MenuItem>
                    <MenuItem value="medium">متوسط</MenuItem>
                    <MenuItem value="high">زیاد</MenuItem>
                    <MenuItem value="critical">بحرانی</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="توضیح مشکل"
                  placeholder="مشکل را به تفصیل توصیف کنید..."
                  value={formData.comment}
                  onChange={(e) => handleFormChange('comment', e.target.value)}
                  required
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>
              لغو
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={submitting}
            >
              {submitting ? 'در حال ذخیره...' : (editingIssue ? 'به‌روزرسانی مشکل' : 'گزارش مشکل')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default IssueReporting;
